/**
 * Shared persistence for validation table column widths (V1 table + V2 excel editor).
 * Stored on the server (DocumentClassApi) and cached in localStorage, always as rem.
 */

import {ClientManager} from "@/singletons/ClientManager";
import {TableColumnWidthV2} from "@/util/TableSettings";

/** Width shape accepted from either table (only id + width matter). */
type ColumnWidthInput = {
	tableFieldId?: number;
	width?: string;
};

export class ValidationColumnWidthStore {

	public static storageKey(tableId: number): string {
		return `squeeze.validation.table.${tableId}.columnWidths`;
	}

	/** Cached widths (rem), or null when nothing valid is cached. */
	public static readCache(tableId: number): TableColumnWidthV2[] | null {
		const cached = localStorage.getItem(this.storageKey(tableId));
		if (!cached) {
			return null;
		}

		try {
			return this.normalize(JSON.parse(cached) as ColumnWidthInput[]);
		} catch {
			return null;
		}
	}

	public static writeCache(tableId: number, widths: ColumnWidthInput[]): void {
		localStorage.setItem(this.storageKey(tableId), JSON.stringify(this.normalize(widths)));
	}

	public static clearCache(tableId: number): void {
		localStorage.removeItem(this.storageKey(tableId));
	}

	public static async fetchFromApi(documentClassId: number, tableId: number): Promise<TableColumnWidthV2[]> {
		const documentClassService = ClientManager.getInstance().squeeze.documentClass;
		return this.normalize(await documentClassService.getAllDocumentClassTableColumnWidths(documentClassId, tableId));
	}

	/**
	 * Saves a width.
	 * When the width is already known to exist (widthExists), it is updated directly via PUT, so a
	 * resize does not fire a redundant POST (which the server rejects with HTTP 400) before the PUT.
	 * For a genuinely new width a POST is sent, still falling back to PUT on HTTP 400 as a safety net
	 * (e.g. when the client cache is out of sync with the server).
	 */
	public static async persistWidth(documentClassId: number, tableId: number, columnId: number, widthInRem: string, widthExists = false): Promise<void> {
		const documentClassService = ClientManager.getInstance().squeeze.documentClass;

		if (widthExists) {
			await documentClassService.putDocumentClassTableColumnWidth(documentClassId, tableId, columnId, widthInRem);
			return;
		}

		try {
			await documentClassService.createDocumentClassTableColumnWidth(documentClassId, tableId, columnId, widthInRem);
		} catch (response: any) {
			if (response?.status !== 400) {
				throw response;
			}
			await documentClassService.putDocumentClassTableColumnWidth(documentClassId, tableId, columnId, widthInRem);
		}
	}

	/** Clears a width server-side (used by "reset widths"). */
	public static async clearWidth(documentClassId: number, tableId: number, columnId: number): Promise<void> {
		const documentClassService = ClientManager.getInstance().squeeze.documentClass;
		await documentClassService.putDocumentClassTableColumnWidth(documentClassId, tableId, columnId, "");
	}

	/** fieldId → width lookup, skipping unset ("0") widths. */
	public static toWidthMap(widths: ColumnWidthInput[]): Map<number, string> {
		const widthMap = new Map<number, string>();
		widths.forEach((width) => {
			if (width.tableFieldId && width.width && width.width !== "0") {
				widthMap.set(width.tableFieldId, width.width);
			}
		});
		return widthMap;
	}

	private static normalize(widths: ColumnWidthInput[]): TableColumnWidthV2[] {
		return widths
			.filter((width): width is TableColumnWidthV2 => typeof width.tableFieldId === "number" && typeof width.width === "string" && width.width.length > 0)
			.map((width) => ({tableFieldId: width.tableFieldId, width: width.width}));
	}

}
